/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse, HttpContext } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { Client } from '../models/client';
import { ClientBankAccount } from '../models/client-bank-account';
import { ClientDocument } from '../models/client-document';
import { ClientDocumentType } from '../models/client-document-type';
import { ClientHistory } from '../models/client-history';
import { ClientInvoiceExportAcceptedResult } from '../models/client-invoice-export-accepted-result';
import { ClientInvoiceExportRequest } from '../models/client-invoice-export-request';
import { ClientInvoiceExportSummary } from '../models/client-invoice-export-summary';
import { ClientPreferences } from '../models/client-preferences';
import { ClientPreferencesUpdate } from '../models/client-preferences-update';
import { ClientRepresentative } from '../models/client-representative';
import { ClientResponsibleSellerReassignmentPreview } from '../models/client-responsible-seller-reassignment-preview';
import { ClientResponsibleSellerUpdate } from '../models/client-responsible-seller-update';
import { ClientResponsibleSellerUpdateResult } from '../models/client-responsible-seller-update-result';
import { ClientType } from '../models/client-type';
import { ClientsResponsePage } from '../models/clients-response-page';
import { CreateYtjDataFetchRequest } from '../models/create-ytj-data-fetch-request';
import { EndCustomerHistory } from '../models/end-customer-history';
import { Error } from '../models/error';
import { FileUrl } from '../models/file-url';
import { IbanOwnership } from '../models/iban-ownership';
import { InvoicesResponsePage } from '../models/invoices-response-page';
import { YtjDataSnapshot } from '../models/ytj-data-snapshot';


/**
 * Clients API
 */
@Injectable({
  providedIn: 'root',
})
export class ClientsService extends BaseService {
  constructor(
    config: ApiConfiguration,
    http: HttpClient
  ) {
    super(config, http);
  }

  /**
   * Path part for operation listClients
   */
  static readonly ListClientsPath = '/clients';

  /**
   * List clients.
   *
   * List clients
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `listClients()` instead.
   *
   * This method doesn't expect any request body.
   */
  listClients$Response(params?: {

    /**
     * Search Query
     */
    'q'?: string;

    /**
     * Client types to include, all if omitted or blank.
     */
    client_types?: Array<ClientType>;

    /**
     * Limit amount of returned results
     */
    limit?: number;

    /**
     * Cursor for the next page of results
     */
    after?: string;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<ClientsResponsePage>> {

    const rb = new RequestBuilder(this.rootUrl, ClientsService.ListClientsPath, 'get');
    if (params) {
      rb.query('q', params['q'], {});
      rb.query('client_types', params.client_types, {"style":"form","explode":false});
      rb.query('limit', params.limit, {});
      rb.query('after', params.after, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ClientsResponsePage>;
      })
    );
  }

  /**
   * List clients.
   *
   * List clients
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `listClients$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  listClients(params?: {

    /**
     * Search Query
     */
    'q'?: string;

    /**
     * Client types to include, all if omitted or blank.
     */
    client_types?: Array<ClientType>;

    /**
     * Limit amount of returned results
     */
    limit?: number;

    /**
     * Cursor for the next page of results
     */
    after?: string;
    context?: HttpContext
  }
): Observable<ClientsResponsePage> {

    return this.listClients$Response(params).pipe(
      map((r: StrictHttpResponse<ClientsResponsePage>) => r.body as ClientsResponsePage)
    );
  }

  /**
   * Path part for operation createClient
   */
  static readonly CreateClientPath = '/clients';

  /**
   * Create client.
   *
   * Create client
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createClient()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createClient$Response(params: {
    context?: HttpContext
    body: Client
  }
): Observable<StrictHttpResponse<Client>> {

    const rb = new RequestBuilder(this.rootUrl, ClientsService.CreateClientPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Client>;
      })
    );
  }

  /**
   * Create client.
   *
   * Create client
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `createClient$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createClient(params: {
    context?: HttpContext
    body: Client
  }
): Observable<Client> {

    return this.createClient$Response(params).pipe(
      map((r: StrictHttpResponse<Client>) => r.body as Client)
    );
  }

  /**
   * Path part for operation lookupIbanOwnership
   */
  static readonly LookupIbanOwnershipPath = '/clients/iban-ownership';

  /**
   * Look up which client holds an IBAN.
   *
   * Answers whether registering an IBAN would collide with the cross-client uniqueness rule, before the registration is attempted. Only bank-account rows without an invoicing-service company business id count as ownership — an invoicing service's account shared between light entrepreneurs is exempt from the rule and is never reported as an owner. Registering your own IBAN is never a conflict: the self-registration exemption is decided by business id or by client id, and either one suffices. Advisory only: the submit-time conflict response remains the authoritative gate.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `lookupIbanOwnership()` instead.
   *
   * This method doesn't expect any request body.
   */
  lookupIbanOwnership$Response(params: {

    /**
     * The IBAN to look up. Compared in compact ISO 13616 form regardless of input formatting.
     */
    iban: string;

    /**
     * The submitting party&#x27;s business id. When it canonically equals the owning client&#x27;s business id, registering the IBAN is not a conflict and no conflict is reported.
     */
    business_id?: string;

    /**
     * The id of the client being edited, when the form was prefilled from an existing client. When it equals the owning client&#x27;s id, registering the IBAN is not a conflict, regardless of the business_id parameter — the operator may be mid-edit in that field. An absent, empty or unparsable value simply grants no exemption.
     */
    client_id?: number;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<IbanOwnership>> {

    const rb = new RequestBuilder(this.rootUrl, ClientsService.LookupIbanOwnershipPath, 'get');
    if (params) {
      rb.query('iban', params.iban, {});
      rb.query('business_id', params.business_id, {});
      rb.query('client_id', params.client_id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<IbanOwnership>;
      })
    );
  }

  /**
   * Look up which client holds an IBAN.
   *
   * Answers whether registering an IBAN would collide with the cross-client uniqueness rule, before the registration is attempted. Only bank-account rows without an invoicing-service company business id count as ownership — an invoicing service's account shared between light entrepreneurs is exempt from the rule and is never reported as an owner. Registering your own IBAN is never a conflict: the self-registration exemption is decided by business id or by client id, and either one suffices. Advisory only: the submit-time conflict response remains the authoritative gate.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `lookupIbanOwnership$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  lookupIbanOwnership(params: {

    /**
     * The IBAN to look up. Compared in compact ISO 13616 form regardless of input formatting.
     */
    iban: string;

    /**
     * The submitting party&#x27;s business id. When it canonically equals the owning client&#x27;s business id, registering the IBAN is not a conflict and no conflict is reported.
     */
    business_id?: string;

    /**
     * The id of the client being edited, when the form was prefilled from an existing client. When it equals the owning client&#x27;s id, registering the IBAN is not a conflict, regardless of the business_id parameter — the operator may be mid-edit in that field. An absent, empty or unparsable value simply grants no exemption.
     */
    client_id?: number;
    context?: HttpContext
  }
): Observable<IbanOwnership> {

    return this.lookupIbanOwnership$Response(params).pipe(
      map((r: StrictHttpResponse<IbanOwnership>) => r.body as IbanOwnership)
    );
  }

  /**
   * Path part for operation getClient
   */
  static readonly GetClientPath = '/clients/{id}';

  /**
   * Get client.
   *
   * Get client
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getClient()` instead.
   *
   * This method doesn't expect any request body.
   */
  getClient$Response(params: {
    id: number;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<Client>> {

    const rb = new RequestBuilder(this.rootUrl, ClientsService.GetClientPath, 'get');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Client>;
      })
    );
  }

  /**
   * Get client.
   *
   * Get client
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getClient$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getClient(params: {
    id: number;
    context?: HttpContext
  }
): Observable<Client> {

    return this.getClient$Response(params).pipe(
      map((r: StrictHttpResponse<Client>) => r.body as Client)
    );
  }

  /**
   * Path part for operation updateClient
   */
  static readonly UpdateClientPath = '/clients/{id}';

  /**
   * Update client.
   *
   * Update client. Requires the `clients:edit` permission.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateClient()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateClient$Response(params: {
    id: number;
    context?: HttpContext
    body: Client
  }
): Observable<StrictHttpResponse<Client>> {

    const rb = new RequestBuilder(this.rootUrl, ClientsService.UpdateClientPath, 'put');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Client>;
      })
    );
  }

  /**
   * Update client.
   *
   * Update client. Requires the `clients:edit` permission.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `updateClient$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateClient(params: {
    id: number;
    context?: HttpContext
    body: Client
  }
): Observable<Client> {

    return this.updateClient$Response(params).pipe(
      map((r: StrictHttpResponse<Client>) => r.body as Client)
    );
  }

  /**
   * Path part for operation deleteClient
   */
  static readonly DeleteClientPath = '/clients/{id}';

  /**
   * Delete client.
   *
   * Delete client
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteClient()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteClient$Response(params: {
    id: number;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, ClientsService.DeleteClientPath, 'delete');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * Delete client.
   *
   * Delete client
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `deleteClient$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteClient(params: {
    id: number;
    context?: HttpContext
  }
): Observable<void> {

    return this.deleteClient$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation createYtjDataFetchForClient
   */
  static readonly CreateYtjDataFetchForClientPath = '/clients/{id}/ytj-data-fetches';

  /**
   * Create a YTJ data fetch for a client.
   *
   * Asynchronously fetches the latest company data from the YTJ service for the given client.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createYtjDataFetchForClient()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createYtjDataFetchForClient$Response(params: {
    id: number;
    context?: HttpContext

    /**
     * Optional body to force a fetch even if recent data exists.
     */
    body?: CreateYtjDataFetchRequest
  }
): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, ClientsService.CreateYtjDataFetchForClientPath, 'post');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * Create a YTJ data fetch for a client.
   *
   * Asynchronously fetches the latest company data from the YTJ service for the given client.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `createYtjDataFetchForClient$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createYtjDataFetchForClient(params: {
    id: number;
    context?: HttpContext

    /**
     * Optional body to force a fetch even if recent data exists.
     */
    body?: CreateYtjDataFetchRequest
  }
): Observable<void> {

    return this.createYtjDataFetchForClient$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation listClientYtjDataSnapshots
   */
  static readonly ListClientYtjDataSnapshotsPath = '/clients/{id}/ytj-data-snapshots';

  /**
   * Get client's YTJ data snapshots.
   *
   * Returns a list of all historical YTJ data snapshots for the given client.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `listClientYtjDataSnapshots()` instead.
   *
   * This method doesn't expect any request body.
   */
  listClientYtjDataSnapshots$Response(params: {
    id: number;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<Array<YtjDataSnapshot>>> {

    const rb = new RequestBuilder(this.rootUrl, ClientsService.ListClientYtjDataSnapshotsPath, 'get');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<YtjDataSnapshot>>;
      })
    );
  }

  /**
   * Get client's YTJ data snapshots.
   *
   * Returns a list of all historical YTJ data snapshots for the given client.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `listClientYtjDataSnapshots$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  listClientYtjDataSnapshots(params: {
    id: number;
    context?: HttpContext
  }
): Observable<Array<YtjDataSnapshot>> {

    return this.listClientYtjDataSnapshots$Response(params).pipe(
      map((r: StrictHttpResponse<Array<YtjDataSnapshot>>) => r.body as Array<YtjDataSnapshot>)
    );
  }

  /**
   * Path part for operation getClientHistory
   */
  static readonly GetClientHistoryPath = '/clients/{id}/history';

  /**
   * Get client history.
   *
   * Get client history
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getClientHistory()` instead.
   *
   * This method doesn't expect any request body.
   */
  getClientHistory$Response(params: {
    id: number;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<Array<ClientHistory>>> {

    const rb = new RequestBuilder(this.rootUrl, ClientsService.GetClientHistoryPath, 'get');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<ClientHistory>>;
      })
    );
  }

  /**
   * Get client history.
   *
   * Get client history
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getClientHistory$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getClientHistory(params: {
    id: number;
    context?: HttpContext
  }
): Observable<Array<ClientHistory>> {

    return this.getClientHistory$Response(params).pipe(
      map((r: StrictHttpResponse<Array<ClientHistory>>) => r.body as Array<ClientHistory>)
    );
  }

  /**
   * Path part for operation updateClientResponsibleSeller
   */
  static readonly UpdateClientResponsibleSellerPath = '/clients/{id}/responsible-seller';

  /**
   * Set, clear or reassign a client's responsible seller.
   *
   * The only write path for a client's responsible seller:
   * `responsible_seller_id` is read-only on the `Client` schema, so the
   * client update and client creation ignore a value riding along.
   *
   * `effective_from` is the mode switch. Without it the call is a plain
   * assignment: it writes the client's seller — `seller_id: null` clears
   * it — and no invoice is touched. With it the call is a reassignment:
   * `expected_previous_seller_id` is then required (`null` meaning the
   * client currently has no seller) and, in one transaction, the call
   * compares it exactly with the client's current seller (`null`
   * matching only a client with no seller; 409 on mismatch), writes the
   * new seller, and re-stamps the client's qualifying invoices.
   *
   * `expected_previous_seller_id` is both that guard and part of the
   * invoice predicate: an invoice qualifies when its
   * `responsible_seller_id` is the same as `expected_previous_seller_id`
   * **or is NULL**, it does not already carry `seller_id`, and its
   * `created` instant **or** its `date` falls on or after
   * `effective_from`, both compared as Europe/Helsinki calendar days. An
   * unattributed invoice was never attributed, so it follows the client's
   * seller; a deliberate attribution to a third seller is left alone. A
   * draft with no `date` qualifies by `created` alone. The invoice's
   * workflow state is irrelevant: attribution is not an edit of the
   * invoice's content, and completed invoices are exactly what the
   * seller reports read.
   *
   * `seller_id` may equal `expected_previous_seller_id`: such a
   * reassignment keeps the seller the client already carries, adding no
   * entry to the client's history, and fills in the qualifying invoices
   * that have none. A reassignment that moves at least one invoice leaves
   * a client comment naming the invoices reassigned from the previous
   * seller apart from those filled in; one that moves nothing writes no
   * comment.
   *
   * `effective_from` must be no later than today and no earlier than two
   * months before today, as Europe/Helsinki calendar days. A date outside
   * those bounds, `expected_previous_seller_id` without `effective_from`,
   * `effective_from` without `expected_previous_seller_id`, and
   * `seller_id: null` together with `effective_from` are all validation
   * errors (422) that modify nothing.
   *
   * Requires the `clients:assign-seller` permission.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateClientResponsibleSeller()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateClientResponsibleSeller$Response(params: {
    id: number;
    context?: HttpContext
    body: ClientResponsibleSellerUpdate
  }
): Observable<StrictHttpResponse<ClientResponsibleSellerUpdateResult>> {

    const rb = new RequestBuilder(this.rootUrl, ClientsService.UpdateClientResponsibleSellerPath, 'put');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ClientResponsibleSellerUpdateResult>;
      })
    );
  }

  /**
   * Set, clear or reassign a client's responsible seller.
   *
   * The only write path for a client's responsible seller:
   * `responsible_seller_id` is read-only on the `Client` schema, so the
   * client update and client creation ignore a value riding along.
   *
   * `effective_from` is the mode switch. Without it the call is a plain
   * assignment: it writes the client's seller — `seller_id: null` clears
   * it — and no invoice is touched. With it the call is a reassignment:
   * `expected_previous_seller_id` is then required (`null` meaning the
   * client currently has no seller) and, in one transaction, the call
   * compares it exactly with the client's current seller (`null`
   * matching only a client with no seller; 409 on mismatch), writes the
   * new seller, and re-stamps the client's qualifying invoices.
   *
   * `expected_previous_seller_id` is both that guard and part of the
   * invoice predicate: an invoice qualifies when its
   * `responsible_seller_id` is the same as `expected_previous_seller_id`
   * **or is NULL**, it does not already carry `seller_id`, and its
   * `created` instant **or** its `date` falls on or after
   * `effective_from`, both compared as Europe/Helsinki calendar days. An
   * unattributed invoice was never attributed, so it follows the client's
   * seller; a deliberate attribution to a third seller is left alone. A
   * draft with no `date` qualifies by `created` alone. The invoice's
   * workflow state is irrelevant: attribution is not an edit of the
   * invoice's content, and completed invoices are exactly what the
   * seller reports read.
   *
   * `seller_id` may equal `expected_previous_seller_id`: such a
   * reassignment keeps the seller the client already carries, adding no
   * entry to the client's history, and fills in the qualifying invoices
   * that have none. A reassignment that moves at least one invoice leaves
   * a client comment naming the invoices reassigned from the previous
   * seller apart from those filled in; one that moves nothing writes no
   * comment.
   *
   * `effective_from` must be no later than today and no earlier than two
   * months before today, as Europe/Helsinki calendar days. A date outside
   * those bounds, `expected_previous_seller_id` without `effective_from`,
   * `effective_from` without `expected_previous_seller_id`, and
   * `seller_id: null` together with `effective_from` are all validation
   * errors (422) that modify nothing.
   *
   * Requires the `clients:assign-seller` permission.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `updateClientResponsibleSeller$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateClientResponsibleSeller(params: {
    id: number;
    context?: HttpContext
    body: ClientResponsibleSellerUpdate
  }
): Observable<ClientResponsibleSellerUpdateResult> {

    return this.updateClientResponsibleSeller$Response(params).pipe(
      map((r: StrictHttpResponse<ClientResponsibleSellerUpdateResult>) => r.body as ClientResponsibleSellerUpdateResult)
    );
  }

  /**
   * Path part for operation getClientResponsibleSellerReassignmentPreview
   */
  static readonly GetClientResponsibleSellerReassignmentPreviewPath = '/clients/{id}/responsible-seller-reassignment-preview';

  /**
   * Preview a responsible-seller reassignment.
   *
   * Dry run of `PUT /clients/{id}/responsible-seller` in reassignment
   * mode. Returns the client's current responsible seller as the previous
   * seller, the invoices a reassignment with these inputs would move, and
   * the invoices of the client inside the date window it would skip
   * because they carry a seller other than the previous seller and
   * `seller_id` — each with the seller it carries, so the operator can
   * see why it is not moving. An invoice in the window that already
   * carries `seller_id` appears in neither list. It writes nothing.
   *
   * The preview applies exactly the predicate and the bounds of the
   * reassignment: an invoice qualifies when its `responsible_seller_id`
   * is the same as the client's current seller **or is NULL**, it does
   * not already carry `seller_id`, and its `created` instant **or** its
   * `date` falls on or after `effective_from`, both compared as
   * Europe/Helsinki calendar days, whatever its workflow state.
   * `seller_id` may be the client's current seller, which previews the
   * fill-in of its unattributed invoices. `effective_from` must be no
   * later than today and no earlier than two months before today.
   *
   * Requires the `clients:assign-seller` permission.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getClientResponsibleSellerReassignmentPreview()` instead.
   *
   * This method doesn't expect any request body.
   */
  getClientResponsibleSellerReassignmentPreview$Response(params: {
    id: number;

    /**
     * The partner the qualifying invoices would be reassigned to. An invoice already carrying it is neither moved nor skipped. A missing or malformed &#x60;seller_id&#x60;, and one that names no partner, one above 2147483647 included, are validation errors (422); the message names the value.
     */
    seller_id: number;

    /**
     * The effective date the predicate is evaluated against, as a Europe/Helsinki calendar day. No later than today and no earlier than two months before today.
     */
    effective_from: string;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<ClientResponsibleSellerReassignmentPreview>> {

    const rb = new RequestBuilder(this.rootUrl, ClientsService.GetClientResponsibleSellerReassignmentPreviewPath, 'get');
    if (params) {
      rb.path('id', params.id, {});
      rb.query('seller_id', params.seller_id, {});
      rb.query('effective_from', params.effective_from, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ClientResponsibleSellerReassignmentPreview>;
      })
    );
  }

  /**
   * Preview a responsible-seller reassignment.
   *
   * Dry run of `PUT /clients/{id}/responsible-seller` in reassignment
   * mode. Returns the client's current responsible seller as the previous
   * seller, the invoices a reassignment with these inputs would move, and
   * the invoices of the client inside the date window it would skip
   * because they carry a seller other than the previous seller and
   * `seller_id` — each with the seller it carries, so the operator can
   * see why it is not moving. An invoice in the window that already
   * carries `seller_id` appears in neither list. It writes nothing.
   *
   * The preview applies exactly the predicate and the bounds of the
   * reassignment: an invoice qualifies when its `responsible_seller_id`
   * is the same as the client's current seller **or is NULL**, it does
   * not already carry `seller_id`, and its `created` instant **or** its
   * `date` falls on or after `effective_from`, both compared as
   * Europe/Helsinki calendar days, whatever its workflow state.
   * `seller_id` may be the client's current seller, which previews the
   * fill-in of its unattributed invoices. `effective_from` must be no
   * later than today and no earlier than two months before today.
   *
   * Requires the `clients:assign-seller` permission.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getClientResponsibleSellerReassignmentPreview$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getClientResponsibleSellerReassignmentPreview(params: {
    id: number;

    /**
     * The partner the qualifying invoices would be reassigned to. An invoice already carrying it is neither moved nor skipped. A missing or malformed &#x60;seller_id&#x60;, and one that names no partner, one above 2147483647 included, are validation errors (422); the message names the value.
     */
    seller_id: number;

    /**
     * The effective date the predicate is evaluated against, as a Europe/Helsinki calendar day. No later than today and no earlier than two months before today.
     */
    effective_from: string;
    context?: HttpContext
  }
): Observable<ClientResponsibleSellerReassignmentPreview> {

    return this.getClientResponsibleSellerReassignmentPreview$Response(params).pipe(
      map((r: StrictHttpResponse<ClientResponsibleSellerReassignmentPreview>) => r.body as ClientResponsibleSellerReassignmentPreview)
    );
  }

  /**
   * Path part for operation updateClientPreferences
   */
  static readonly UpdateClientPreferencesPath = '/clients/{id}/preferences';

  /**
   * Set a client's preferences.
   *
   * The only write path for a client's client preferences: `preferences`
   * is read-only on the `Client` schema, so the client update ignores a
   * value riding along.
   *
   * Each property of the body sets that preference; an absent property
   * leaves it as it is. A value equal to the preference's effective value
   * changes nothing and writes nothing. Every change writes a trace
   * comment on the client, authored by the caller, naming the preference
   * and its new value. Changing a preference never touches an outgoing
   * payment already created: callers read the effective value when they
   * act.
   *
   * A preference locked for the client's type (see
   * `ClientPreference.locked`) accepts only its locked value.
   *
   * `error_code` values returned with `422`:
   *
   * - `client_preference_locked` — the body sets a preference that the
   *   client's type locks to a value other than the one given. Nothing
   *   changes. `details.preference` names the preference (its property
   *   name, e.g. `original_reference_payments`) and
   *   `details.locked_value` is the value it is locked to.
   *
   * Requires the `clients:edit-preferences` permission.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateClientPreferences()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateClientPreferences$Response(params: {
    id: number;
    context?: HttpContext
    body: ClientPreferencesUpdate
  }
): Observable<StrictHttpResponse<ClientPreferences>> {

    const rb = new RequestBuilder(this.rootUrl, ClientsService.UpdateClientPreferencesPath, 'put');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ClientPreferences>;
      })
    );
  }

  /**
   * Set a client's preferences.
   *
   * The only write path for a client's client preferences: `preferences`
   * is read-only on the `Client` schema, so the client update ignores a
   * value riding along.
   *
   * Each property of the body sets that preference; an absent property
   * leaves it as it is. A value equal to the preference's effective value
   * changes nothing and writes nothing. Every change writes a trace
   * comment on the client, authored by the caller, naming the preference
   * and its new value. Changing a preference never touches an outgoing
   * payment already created: callers read the effective value when they
   * act.
   *
   * A preference locked for the client's type (see
   * `ClientPreference.locked`) accepts only its locked value.
   *
   * `error_code` values returned with `422`:
   *
   * - `client_preference_locked` — the body sets a preference that the
   *   client's type locks to a value other than the one given. Nothing
   *   changes. `details.preference` names the preference (its property
   *   name, e.g. `original_reference_payments`) and
   *   `details.locked_value` is the value it is locked to.
   *
   * Requires the `clients:edit-preferences` permission.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `updateClientPreferences$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateClientPreferences(params: {
    id: number;
    context?: HttpContext
    body: ClientPreferencesUpdate
  }
): Observable<ClientPreferences> {

    return this.updateClientPreferences$Response(params).pipe(
      map((r: StrictHttpResponse<ClientPreferences>) => r.body as ClientPreferences)
    );
  }

  /**
   * Path part for operation getEndCustomerHistory
   */
  static readonly GetEndCustomerHistoryPath = '/end-customers/{id}/history';

  /**
   * Get end customer history.
   *
   * Get end customer history
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getEndCustomerHistory()` instead.
   *
   * This method doesn't expect any request body.
   */
  getEndCustomerHistory$Response(params: {
    id: number;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<Array<EndCustomerHistory>>> {

    const rb = new RequestBuilder(this.rootUrl, ClientsService.GetEndCustomerHistoryPath, 'get');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<EndCustomerHistory>>;
      })
    );
  }

  /**
   * Get end customer history.
   *
   * Get end customer history
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getEndCustomerHistory$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getEndCustomerHistory(params: {
    id: number;
    context?: HttpContext
  }
): Observable<Array<EndCustomerHistory>> {

    return this.getEndCustomerHistory$Response(params).pipe(
      map((r: StrictHttpResponse<Array<EndCustomerHistory>>) => r.body as Array<EndCustomerHistory>)
    );
  }

  /**
   * Path part for operation createClientBankAccount
   */
  static readonly CreateClientBankAccountPath = '/clients/{client_id}/bank-accounts';

  /**
   * Create a bank account for the client.
   *
   * Create a bank account for the client. Requires the `clients:edit`
   * permission.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createClientBankAccount()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createClientBankAccount$Response(params: {
    client_id: number;
    context?: HttpContext
    body: ClientBankAccount
  }
): Observable<StrictHttpResponse<ClientBankAccount>> {

    const rb = new RequestBuilder(this.rootUrl, ClientsService.CreateClientBankAccountPath, 'post');
    if (params) {
      rb.path('client_id', params.client_id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ClientBankAccount>;
      })
    );
  }

  /**
   * Create a bank account for the client.
   *
   * Create a bank account for the client. Requires the `clients:edit`
   * permission.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `createClientBankAccount$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createClientBankAccount(params: {
    client_id: number;
    context?: HttpContext
    body: ClientBankAccount
  }
): Observable<ClientBankAccount> {

    return this.createClientBankAccount$Response(params).pipe(
      map((r: StrictHttpResponse<ClientBankAccount>) => r.body as ClientBankAccount)
    );
  }

  /**
   * Path part for operation updateClientBankAccount
   */
  static readonly UpdateClientBankAccountPath = '/clients/{client_id}/bank-accounts/{id}';

  /**
   * Update Client Bank Account.
   *
   * Update Client Bank Account
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateClientBankAccount()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateClientBankAccount$Response(params: {
    client_id: number;
    id: number;
    context?: HttpContext
    body: ClientBankAccount
  }
): Observable<StrictHttpResponse<ClientBankAccount>> {

    const rb = new RequestBuilder(this.rootUrl, ClientsService.UpdateClientBankAccountPath, 'put');
    if (params) {
      rb.path('client_id', params.client_id, {});
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ClientBankAccount>;
      })
    );
  }

  /**
   * Update Client Bank Account.
   *
   * Update Client Bank Account
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `updateClientBankAccount$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateClientBankAccount(params: {
    client_id: number;
    id: number;
    context?: HttpContext
    body: ClientBankAccount
  }
): Observable<ClientBankAccount> {

    return this.updateClientBankAccount$Response(params).pipe(
      map((r: StrictHttpResponse<ClientBankAccount>) => r.body as ClientBankAccount)
    );
  }

  /**
   * Path part for operation deleteClientBankAccount
   */
  static readonly DeleteClientBankAccountPath = '/clients/{client_id}/bank-accounts/{id}';

  /**
   * Delete the bank account.
   *
   * Delete the bank account
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteClientBankAccount()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteClientBankAccount$Response(params: {
    client_id: number;
    id: number;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, ClientsService.DeleteClientBankAccountPath, 'delete');
    if (params) {
      rb.path('client_id', params.client_id, {});
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * Delete the bank account.
   *
   * Delete the bank account
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `deleteClientBankAccount$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteClientBankAccount(params: {
    client_id: number;
    id: number;
    context?: HttpContext
  }
): Observable<void> {

    return this.deleteClientBankAccount$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation getClientInvoices
   */
  static readonly GetClientInvoicesPath = '/clients/{client_id}/invoices';

  /**
   * Get invoices for a client.
   *
   * Get invoices for a client
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getClientInvoices()` instead.
   *
   * This method doesn't expect any request body.
   */
  getClientInvoices$Response(params: {
    client_id: number;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<InvoicesResponsePage>> {

    const rb = new RequestBuilder(this.rootUrl, ClientsService.GetClientInvoicesPath, 'get');
    if (params) {
      rb.path('client_id', params.client_id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<InvoicesResponsePage>;
      })
    );
  }

  /**
   * Get invoices for a client.
   *
   * Get invoices for a client
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getClientInvoices$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getClientInvoices(params: {
    client_id: number;
    context?: HttpContext
  }
): Observable<InvoicesResponsePage> {

    return this.getClientInvoices$Response(params).pipe(
      map((r: StrictHttpResponse<InvoicesResponsePage>) => r.body as InvoicesResponsePage)
    );
  }

  /**
   * Path part for operation getClientDocuments
   */
  static readonly GetClientDocumentsPath = '/clients/{client_id}/documents';

  /**
   * Get documents for a client.
   *
   * Get documents for a client
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getClientDocuments()` instead.
   *
   * This method doesn't expect any request body.
   */
  getClientDocuments$Response(params: {
    client_id: number;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<Array<ClientDocument>>> {

    const rb = new RequestBuilder(this.rootUrl, ClientsService.GetClientDocumentsPath, 'get');
    if (params) {
      rb.path('client_id', params.client_id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<ClientDocument>>;
      })
    );
  }

  /**
   * Get documents for a client.
   *
   * Get documents for a client
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getClientDocuments$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getClientDocuments(params: {
    client_id: number;
    context?: HttpContext
  }
): Observable<Array<ClientDocument>> {

    return this.getClientDocuments$Response(params).pipe(
      map((r: StrictHttpResponse<Array<ClientDocument>>) => r.body as Array<ClientDocument>)
    );
  }

  /**
   * Path part for operation createClientDocument
   */
  static readonly CreateClientDocumentPath = '/clients/{client_id}/documents';

  /**
   * Create a document for a client.
   *
   * Create a document for a client
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createClientDocument()` instead.
   *
   * This method sends `multipart/form-data` and handles request body of type `multipart/form-data`.
   */
  createClientDocument$Response(params: {
    client_id: number;
    context?: HttpContext
    body: {
'file'?: Blob;
}
  }
): Observable<StrictHttpResponse<ClientDocument>> {

    const rb = new RequestBuilder(this.rootUrl, ClientsService.CreateClientDocumentPath, 'post');
    if (params) {
      rb.path('client_id', params.client_id, {});
      rb.body(params.body, 'multipart/form-data');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ClientDocument>;
      })
    );
  }

  /**
   * Create a document for a client.
   *
   * Create a document for a client
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `createClientDocument$Response()` instead.
   *
   * This method sends `multipart/form-data` and handles request body of type `multipart/form-data`.
   */
  createClientDocument(params: {
    client_id: number;
    context?: HttpContext
    body: {
'file'?: Blob;
}
  }
): Observable<ClientDocument> {

    return this.createClientDocument$Response(params).pipe(
      map((r: StrictHttpResponse<ClientDocument>) => r.body as ClientDocument)
    );
  }

  /**
   * Path part for operation getClientDocument
   */
  static readonly GetClientDocumentPath = '/clients/{client_id}/documents/{id}';

  /**
   * Get metadata for one document.
   *
   * Get metadata for one document
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getClientDocument()` instead.
   *
   * This method doesn't expect any request body.
   */
  getClientDocument$Response(params: {
    client_id: number;
    id: number;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<ClientDocument>> {

    const rb = new RequestBuilder(this.rootUrl, ClientsService.GetClientDocumentPath, 'get');
    if (params) {
      rb.path('client_id', params.client_id, {});
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ClientDocument>;
      })
    );
  }

  /**
   * Get metadata for one document.
   *
   * Get metadata for one document
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getClientDocument$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getClientDocument(params: {
    client_id: number;
    id: number;
    context?: HttpContext
  }
): Observable<ClientDocument> {

    return this.getClientDocument$Response(params).pipe(
      map((r: StrictHttpResponse<ClientDocument>) => r.body as ClientDocument)
    );
  }

  /**
   * Path part for operation updateClientDocument
   */
  static readonly UpdateClientDocumentPath = '/clients/{client_id}/documents/{id}';

  /**
   * Update metadata for one document.
   *
   * Update metadata for one document
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateClientDocument()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateClientDocument$Response(params: {
    client_id: number;
    id: number;
    context?: HttpContext
    body: ClientDocument
  }
): Observable<StrictHttpResponse<ClientDocument>> {

    const rb = new RequestBuilder(this.rootUrl, ClientsService.UpdateClientDocumentPath, 'put');
    if (params) {
      rb.path('client_id', params.client_id, {});
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ClientDocument>;
      })
    );
  }

  /**
   * Update metadata for one document.
   *
   * Update metadata for one document
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `updateClientDocument$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateClientDocument(params: {
    client_id: number;
    id: number;
    context?: HttpContext
    body: ClientDocument
  }
): Observable<ClientDocument> {

    return this.updateClientDocument$Response(params).pipe(
      map((r: StrictHttpResponse<ClientDocument>) => r.body as ClientDocument)
    );
  }

  /**
   * Path part for operation deleteClientDocument
   */
  static readonly DeleteClientDocumentPath = '/clients/{client_id}/documents/{id}';

  /**
   * Delete the document.
   *
   * Delete the document
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteClientDocument()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteClientDocument$Response(params: {
    client_id: number;
    id: number;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, ClientsService.DeleteClientDocumentPath, 'delete');
    if (params) {
      rb.path('client_id', params.client_id, {});
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * Delete the document.
   *
   * Delete the document
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `deleteClientDocument$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteClientDocument(params: {
    client_id: number;
    id: number;
    context?: HttpContext
  }
): Observable<void> {

    return this.deleteClientDocument$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation getClientDocumentUrl
   */
  static readonly GetClientDocumentUrlPath = '/clients/{client_id}/documents/{id}/file';

  /**
   * Get a link to the contents of the document.
   *
   * Get a link to the contents of the document
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getClientDocumentUrl()` instead.
   *
   * This method doesn't expect any request body.
   */
  getClientDocumentUrl$Response(params: {
    client_id: number;
    id: number;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<FileUrl>> {

    const rb = new RequestBuilder(this.rootUrl, ClientsService.GetClientDocumentUrlPath, 'get');
    if (params) {
      rb.path('client_id', params.client_id, {});
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<FileUrl>;
      })
    );
  }

  /**
   * Get a link to the contents of the document.
   *
   * Get a link to the contents of the document
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getClientDocumentUrl$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getClientDocumentUrl(params: {
    client_id: number;
    id: number;
    context?: HttpContext
  }
): Observable<FileUrl> {

    return this.getClientDocumentUrl$Response(params).pipe(
      map((r: StrictHttpResponse<FileUrl>) => r.body as FileUrl)
    );
  }

  /**
   * Path part for operation getClientDocumentTypes
   */
  static readonly GetClientDocumentTypesPath = '/client-document-types';

  /**
   * Get list of client document types.
   *
   * Get list of client document types
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getClientDocumentTypes()` instead.
   *
   * This method doesn't expect any request body.
   */
  getClientDocumentTypes$Response(params?: {
    context?: HttpContext
  }
): Observable<StrictHttpResponse<Array<ClientDocumentType>>> {

    const rb = new RequestBuilder(this.rootUrl, ClientsService.GetClientDocumentTypesPath, 'get');
    if (params) {
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<ClientDocumentType>>;
      })
    );
  }

  /**
   * Get list of client document types.
   *
   * Get list of client document types
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getClientDocumentTypes$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getClientDocumentTypes(params?: {
    context?: HttpContext
  }
): Observable<Array<ClientDocumentType>> {

    return this.getClientDocumentTypes$Response(params).pipe(
      map((r: StrictHttpResponse<Array<ClientDocumentType>>) => r.body as Array<ClientDocumentType>)
    );
  }

  /**
   * Path part for operation getClientRepresentatives
   */
  static readonly GetClientRepresentativesPath = '/clients/{client_id}/representatives';

  /**
   * Get representatives for a client.
   *
   * Returns a list of client representatives for the given client.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getClientRepresentatives()` instead.
   *
   * This method doesn't expect any request body.
   */
  getClientRepresentatives$Response(params: {
    client_id: number;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<Array<ClientRepresentative>>> {

    const rb = new RequestBuilder(this.rootUrl, ClientsService.GetClientRepresentativesPath, 'get');
    if (params) {
      rb.path('client_id', params.client_id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<ClientRepresentative>>;
      })
    );
  }

  /**
   * Get representatives for a client.
   *
   * Returns a list of client representatives for the given client.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getClientRepresentatives$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getClientRepresentatives(params: {
    client_id: number;
    context?: HttpContext
  }
): Observable<Array<ClientRepresentative>> {

    return this.getClientRepresentatives$Response(params).pipe(
      map((r: StrictHttpResponse<Array<ClientRepresentative>>) => r.body as Array<ClientRepresentative>)
    );
  }

  /**
   * Path part for operation createClientRepresentative
   */
  static readonly CreateClientRepresentativePath = '/clients/{client_id}/representatives';

  /**
   * Create a representative for a client.
   *
   * Create a representative for a client
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createClientRepresentative()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createClientRepresentative$Response(params: {
    client_id: number;
    context?: HttpContext
    body: ClientRepresentative
  }
): Observable<StrictHttpResponse<ClientRepresentative>> {

    const rb = new RequestBuilder(this.rootUrl, ClientsService.CreateClientRepresentativePath, 'post');
    if (params) {
      rb.path('client_id', params.client_id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ClientRepresentative>;
      })
    );
  }

  /**
   * Create a representative for a client.
   *
   * Create a representative for a client
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `createClientRepresentative$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createClientRepresentative(params: {
    client_id: number;
    context?: HttpContext
    body: ClientRepresentative
  }
): Observable<ClientRepresentative> {

    return this.createClientRepresentative$Response(params).pipe(
      map((r: StrictHttpResponse<ClientRepresentative>) => r.body as ClientRepresentative)
    );
  }

  /**
   * Path part for operation getClientRepresentative
   */
  static readonly GetClientRepresentativePath = '/clients/{client_id}/representatives/{id}';

  /**
   * Get one representative.
   *
   * Get one representative
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getClientRepresentative()` instead.
   *
   * This method doesn't expect any request body.
   */
  getClientRepresentative$Response(params: {
    client_id: number;
    id: number;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<ClientRepresentative>> {

    const rb = new RequestBuilder(this.rootUrl, ClientsService.GetClientRepresentativePath, 'get');
    if (params) {
      rb.path('client_id', params.client_id, {});
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ClientRepresentative>;
      })
    );
  }

  /**
   * Get one representative.
   *
   * Get one representative
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getClientRepresentative$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getClientRepresentative(params: {
    client_id: number;
    id: number;
    context?: HttpContext
  }
): Observable<ClientRepresentative> {

    return this.getClientRepresentative$Response(params).pipe(
      map((r: StrictHttpResponse<ClientRepresentative>) => r.body as ClientRepresentative)
    );
  }

  /**
   * Path part for operation updateClientRepresentative
   */
  static readonly UpdateClientRepresentativePath = '/clients/{client_id}/representatives/{id}';

  /**
   * Update one representative.
   *
   * Update one representative
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateClientRepresentative()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateClientRepresentative$Response(params: {
    client_id: number;
    id: number;
    context?: HttpContext
    body: ClientRepresentative
  }
): Observable<StrictHttpResponse<ClientRepresentative>> {

    const rb = new RequestBuilder(this.rootUrl, ClientsService.UpdateClientRepresentativePath, 'put');
    if (params) {
      rb.path('client_id', params.client_id, {});
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ClientRepresentative>;
      })
    );
  }

  /**
   * Update one representative.
   *
   * Update one representative
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `updateClientRepresentative$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateClientRepresentative(params: {
    client_id: number;
    id: number;
    context?: HttpContext
    body: ClientRepresentative
  }
): Observable<ClientRepresentative> {

    return this.updateClientRepresentative$Response(params).pipe(
      map((r: StrictHttpResponse<ClientRepresentative>) => r.body as ClientRepresentative)
    );
  }

  /**
   * Path part for operation deleteClientRepresentative
   */
  static readonly DeleteClientRepresentativePath = '/clients/{client_id}/representatives/{id}';

  /**
   * Delete the representative.
   *
   * This endpoint is not implemented and will return a 501 error. Deleting client representatives is not supported.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteClientRepresentative()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteClientRepresentative$Response(params: {
    client_id: number;
    id: number;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<Error>> {

    const rb = new RequestBuilder(this.rootUrl, ClientsService.DeleteClientRepresentativePath, 'delete');
    if (params) {
      rb.path('client_id', params.client_id, {});
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Error>;
      })
    );
  }

  /**
   * Delete the representative.
   *
   * This endpoint is not implemented and will return a 501 error. Deleting client representatives is not supported.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `deleteClientRepresentative$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteClientRepresentative(params: {
    client_id: number;
    id: number;
    context?: HttpContext
  }
): Observable<Error> {

    return this.deleteClientRepresentative$Response(params).pipe(
      map((r: StrictHttpResponse<Error>) => r.body as Error)
    );
  }

  /**
   * Path part for operation listClientInvoiceExports
   */
  static readonly ListClientInvoiceExportsPath = '/clients/{client_id}/exports';

  /**
   * List a client's exports, newest first.
   *
   * Returns this client's export rows ordered by `requested_at DESC`.
   * Includes terminal-state rows (`expired`, `failed`) so the Console
   * can show the historical audit trail.
   *
   * Requires the `exports:client-invoice` permission (granted to the
   * `User` and `Administrator` roles).
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `listClientInvoiceExports()` instead.
   *
   * This method doesn't expect any request body.
   */
  listClientInvoiceExports$Response(params: {
    client_id: number;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<{
'exports': Array<ClientInvoiceExportSummary>;
}>> {

    const rb = new RequestBuilder(this.rootUrl, ClientsService.ListClientInvoiceExportsPath, 'get');
    if (params) {
      rb.path('client_id', params.client_id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<{
        'exports': Array<ClientInvoiceExportSummary>;
        }>;
      })
    );
  }

  /**
   * List a client's exports, newest first.
   *
   * Returns this client's export rows ordered by `requested_at DESC`.
   * Includes terminal-state rows (`expired`, `failed`) so the Console
   * can show the historical audit trail.
   *
   * Requires the `exports:client-invoice` permission (granted to the
   * `User` and `Administrator` roles).
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `listClientInvoiceExports$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  listClientInvoiceExports(params: {
    client_id: number;
    context?: HttpContext
  }
): Observable<{
'exports': Array<ClientInvoiceExportSummary>;
}> {

    return this.listClientInvoiceExports$Response(params).pipe(
      map((r: StrictHttpResponse<{
'exports': Array<ClientInvoiceExportSummary>;
}>) => r.body as {
'exports': Array<ClientInvoiceExportSummary>;
})
    );
  }

  /**
   * Path part for operation createClientInvoiceExport
   */
  static readonly CreateClientInvoiceExportPath = '/clients/{client_id}/exports';

  /**
   * Start a client-invoice PDF export.
   *
   * Starts a ClientInvoiceBundleWorkflow that resolves InvoiceAttachments
   * for the given client + date range + scope set, zips them into a
   * single archive (with `manifest.csv` at the root), and stores it in
   * Azure Blob storage with a 7-day TTL. The endpoint inserts a `pending`
   * row into `exports` and starts the workflow; on Temporal start failure
   * the row is deleted, so the API does not leave orphaned pending rows.
   *
   * Requires the `exports:client-invoice` permission (granted to the
   * `User` and `Administrator` roles).
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createClientInvoiceExport()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createClientInvoiceExport$Response(params: {
    client_id: number;
    context?: HttpContext
    body: ClientInvoiceExportRequest
  }
): Observable<StrictHttpResponse<ClientInvoiceExportAcceptedResult>> {

    const rb = new RequestBuilder(this.rootUrl, ClientsService.CreateClientInvoiceExportPath, 'post');
    if (params) {
      rb.path('client_id', params.client_id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ClientInvoiceExportAcceptedResult>;
      })
    );
  }

  /**
   * Start a client-invoice PDF export.
   *
   * Starts a ClientInvoiceBundleWorkflow that resolves InvoiceAttachments
   * for the given client + date range + scope set, zips them into a
   * single archive (with `manifest.csv` at the root), and stores it in
   * Azure Blob storage with a 7-day TTL. The endpoint inserts a `pending`
   * row into `exports` and starts the workflow; on Temporal start failure
   * the row is deleted, so the API does not leave orphaned pending rows.
   *
   * Requires the `exports:client-invoice` permission (granted to the
   * `User` and `Administrator` roles).
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `createClientInvoiceExport$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createClientInvoiceExport(params: {
    client_id: number;
    context?: HttpContext
    body: ClientInvoiceExportRequest
  }
): Observable<ClientInvoiceExportAcceptedResult> {

    return this.createClientInvoiceExport$Response(params).pipe(
      map((r: StrictHttpResponse<ClientInvoiceExportAcceptedResult>) => r.body as ClientInvoiceExportAcceptedResult)
    );
  }

}
